import React, { useMemo, useRef, useState } from 'react'
import { Form } from '../../../lib/ui/form'
import { Separator } from '../../../lib/ui/separator'
import { useProgramSurveyForm } from './useProgramSurveyForm'
import { Button } from '~/lib/ui/button'
import { RatingField } from '../RatingField'
import { TextareaField } from '../TextareaField'
import { DatePickerField } from '../DatePickerField'
import { MultipleChoiceField } from '../MultipleChoiceField'
import { MultiPartField } from '../MultiPartField'

export default function MidProgramSurveyForm({
  questions,
  choices,
  candidateId,
  companyId,
  setError,
  setThankYou,
}) {
  const { form, onSubmit } = useProgramSurveyForm({
    surveyType: 'program_survey_mid_program',
    questions,
    candidate_id: candidateId,
    company_id: companyId,
    setError,
    setThankYou,
  })

  const [showDatePicker, setShowDatePicker] = useState(false)
  const [datePickerAnnouncement, setDatePickerAnnouncement] = useState('')
  const datePickerContainerRef = useRef(null)

  const conditionalDateQuestionName = useMemo(() => {
    const dateQuestion = Object.values(questions).find(
      (question) => question.type === 'date',
    )

    return dateQuestion?.name
  }, [questions])

  React.useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (
        name === questions.where_are_you_currently_career_transition.name &&
        value[questions.where_are_you_currently_career_transition.name] === 'd'
      ) {
        setShowDatePicker(true)
        setDatePickerAnnouncement('Date input question is now visible.')
      } else if (
        name === questions.where_are_you_currently_career_transition.name &&
        value[questions.where_are_you_currently_career_transition.name] !== 'd'
      ) {
        setShowDatePicker(false)
        setDatePickerAnnouncement('Date input question hidden.')
      }
    })
    return () => subscription.unsubscribe()
  }, [form, questions.where_are_you_currently_career_transition.name])

  React.useEffect(() => {
    if (!showDatePicker || !datePickerContainerRef.current) return

    // Move focus to the revealed field group so keyboard and screen reader users are notified of context change.
    datePickerContainerRef.current.focus()
  }, [showDatePicker, conditionalDateQuestionName])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="sr-only" aria-live="polite" role="status">
          {datePickerAnnouncement}
        </div>
        {Object.entries(questions).map(([key, question]) => {
          if (question.type === 'boolean' || question.type === 'rating') {
            return (
              <div className="my-6 rounded-xl bg-white p-6" key={question.name}>
                <RatingField
                  name={question.name}
                  label={question.name}
                  choices={choices[key]}
                  control={form.control}
                  oneToFive={true}
                  programSurvey={true}
                />
              </div>
            )
          } else if (question.type === 'multiple_choice') {
            return (
              <div className="my-6 rounded-xl bg-white p-6" key={question.name}>
                <MultipleChoiceField
                  name={question.name}
                  label={question.name}
                  choices={choices[key]}
                  control={form.control}
                  programSurvey={true}
                />
              </div>
            )
          } else if (question.type === 'multi-part') {
            return (
              <div className="my-6 rounded-xl bg-white p-6" key={question.name}>
                <MultiPartField
                  name={question.name}
                  label={question.name}
                  questions={question.sub_questions}
                  choices={choices[key]}
                  control={form.control}
                  programSurvey={true}
                />
              </div>
            )
          } else if (question.type === 'star') {
            return (
              <div className="my-6 rounded-xl bg-white p-6" key={question.name}>
                <RatingField
                  name={question.name}
                  label={question.name}
                  choices={choices[key]}
                  control={form.control}
                  yesOrNo={true}
                  programSurvey={true}
                />
              </div>
            )
          } else if (question.type === 'date') {
            if (showDatePicker) {
              return (
                <div
                  className="my-6 rounded-xl bg-white p-6"
                  key={question.name}
                  ref={datePickerContainerRef}
                  tabIndex={-1}
                >
                  <DatePickerField
                    name={question.name}
                    label={question.name}
                    control={form.control}
                    programSurvey={true}
                  />
                </div>
              )
            }
          } else if (question.type === 'text') {
            return (
              <div className="my-6 rounded-xl bg-white p-6" key={question.name}>
                <TextareaField
                  name={question.name}
                  label={question.name}
                  control={form.control}
                  programSurvey={true}
                />
              </div>
            )
          }
        })}
        <Separator className="my-8" />

        <div className="flex w-full justify-center">
          <Button type="submit">Submit</Button>
        </div>
      </form>
    </Form>
  )
}
